import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from '@/lib/i18n';
import { METHODOLOGY, type MethodologyId } from '@/lib/methodology';
import { cn } from '@/lib/utils';

const PANEL_W = 288; // w-72

/**
 * A tiny info dot next to the number it qualifies. The popover is portaled to
 * <body> with fixed positioning so it is never clipped by a table's overflow
 * container or a short overflow-hidden bar (the dots live inside both). Clamped
 * to the viewport; closes on outside-click or Escape; repositions on scroll/resize.
 */
export function CaveatRef({ id, className }: { id: MethodologyId; className?: string }) {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const entry = METHODOLOGY[id];

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.min(
        Math.max(8, r.left + r.width / 2 - PANEL_W / 2),
        window.innerWidth - PANEL_W - 8,
      );
      setPos({ top: r.bottom + 6, left });
    };
    place();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  return (
    <span className={cn('inline-flex align-middle', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={entry.title[locale]}
        className="text-fg-subtle transition-fast hover:text-fg"
      >
        <Info className="size-3.5" aria-hidden />
      </button>
      {open
        ? createPortal(
            <div
              ref={popRef}
              role="tooltip"
              style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_W }}
              className="z-50 rounded-md border border-border bg-surface p-3 text-left shadow-pop"
            >
              <span className="block text-label font-semibold text-fg">{entry.title[locale]}</span>
              <span className="mt-1 block text-label leading-relaxed text-fg-muted">
                {entry.body[locale]}
              </span>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
